import React, { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useSidebar } from '../../contexts/SidebarContext';
import CustomDropdown from '../ui/CustomDropdown';
import SectionPanel from './SectionPanel';
import SkillCard from './SkillCard';
import SkillDetailModal from './SkillDetailModal';
import UserSkillModal from './UserSkillModal';
import { FileText, Plus, ArrowRight, Search, Star } from 'lucide-react';
import { userSkillFromPrompt, tagsToCsv, tagsToArray } from './skillsUtils';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Minhas Skills: os prompts do usuário (tabela prompts) com a MESMA
 * experiência das skills do sistema (card adaptativo, detalhe com
 * instruções e tutorial, copiar direto do card). Painel no padrão TTS.
 * Mantém o vínculo com canal, favoritos e a busca. Aberta a todos.
 */
const MySkillsSection = ({ canais = [], prompts = [], loading = false, onChanged, isPrivate = false }) => {
  const { t } = useTranslation();
  const { isMobile } = useSidebar();

  const [selectedCanal, setSelectedCanal] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [viewingSkill, setViewingSkill] = useState(null);
  const [userModal, setUserModal] = useState({ open: false, editing: null });

  const rawById = useMemo(() => {
    const map = {};
    for (const p of prompts) map[p.id] = p;
    return map;
  }, [prompts]);

  const canalNameById = useMemo(() => {
    const map = {};
    for (const c of canais) map[c.id] = c.nome;
    return map;
  }, [canais]);

  const canalLabelOf = useCallback(
    (skill) => (skill.canal_id ? (canalNameById[skill.canal_id] || t('prompts.unknown_channel')) : t('skills.save_dialog.global')),
    [canalNameById, t]
  );

  const userSkills = useMemo(() => prompts.map(userSkillFromPrompt), [prompts]);

  const filtered = useMemo(() => {
    let list = userSkills;
    if (onlyFavorites) list = list.filter((s) => s.favorito);
    if (selectedCanal !== 'all') {
      if (selectedCanal === 'global') list = list.filter((s) => !s.canal_id);
      else list = list.filter((s) => s.canal_id === selectedCanal);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((s) =>
        s.nome.toLowerCase().includes(q) ||
        s.categoria.toLowerCase().includes(q) ||
        s.conteudo.toLowerCase().includes(q) ||
        s.tags.some((tg) => tg.toLowerCase().includes(q))
      );
    }
    // Favoritos primeiro (comportamento existente da página)
    return [...list].sort((a, b) => {
      if (a.favorito && !b.favorito) return -1;
      if (!a.favorito && b.favorito) return 1;
      return 0;
    });
  }, [userSkills, onlyFavorites, selectedCanal, searchQuery]);

  const canalFilterOptions = [
    { value: 'all', label: t('prompts.all_channels') },
    { value: 'global', label: t('prompts.global_prompts') },
    ...canais.map((c) => ({ value: c.id, label: c.nome })),
  ];

  const handleCopy = useCallback(async (skill) => {
    try {
      await navigator.clipboard.writeText(skill.conteudo);
      toast.success(t('skills.detail.copied'));
    } catch (e) {
      toast.error(t('skills.detail.error_copy'));
      throw e;
    }
  }, [t]);

  const handleToggleFavorite = useCallback(async (skill) => {
    const raw = rawById[skill.id];
    if (!raw) return;
    try {
      await axios.put(`${API}/prompts/${skill.id}`, {
        canal_id: raw.canal_id,
        nome: raw.nome,
        categoria: raw.categoria,
        conteudo: raw.conteudo,
        anotacoes: raw.anotacoes,
        idioma_sugerido: raw.idioma_sugerido,
        tags: typeof raw.tags === 'string' ? raw.tags : tagsToCsv(tagsToArray(raw.tags)),
        favorito: !raw.favorito,
      });
      toast.success(!raw.favorito ? t('skills.my.fav_added') : t('skills.my.fav_removed'));
      onChanged();
    } catch (e) {
      toast.error(t('skills.admin.error_save'));
    }
  }, [rawById, onChanged, t]);

  const handleDelete = useCallback(async (skill) => {
    if (!window.confirm(t('skills.my.delete_confirm'))) return;
    try {
      await axios.delete(`${API}/prompts/${skill.id}`);
      toast.success(t('skills.my.deleted'));
      onChanged();
    } catch (e) {
      toast.error(t('skills.admin.error_save'));
    }
  }, [onChanged, t]);

  const openEdit = useCallback((skill) => {
    const raw = rawById[skill.id];
    if (raw) setUserModal({ open: true, editing: raw });
  }, [rawById]);

  // Detalhe do skill do usuário resolve na hora (conteudo já veio na lista).
  const getDetail = useCallback((skill) => ({
    conteudo: skill.conteudo || '',
    instrucoes: skill.instrucoes || '',
    video_url: skill.video_url || '',
  }), []);

  // Biblioteca com muitas skills: cards menores, mais colunas por linha.
  const gridStyle = {
    display: 'grid', gap: '12px',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(205px, 1fr))',
  };

  return (
    <SectionPanel
      icon={FileText}
      title={t('skills.my.title')}
      count={prompts.length > 0 ? prompts.length : null}
      collapsible
      storageKey="my-skills"
      actions={
        <button
          type="button"
          className="dp-btn-cta dp-btn-cta--sm"
          onClick={() => setUserModal({ open: true, editing: null })}
        >
          <Plus size={14} />
          {t('skills.my.new_skill')}
          <ArrowRight size={14} />
        </button>
      }
    >
      <p style={{
        margin: '0 0 14px', fontSize: '12px', color: 'var(--dp-text-tertiary)',
        fontFamily: 'var(--dp-font)', lineHeight: 1.5,
      }}>
        {t('skills.my.subtitle')}
      </p>

      {/* Toolbar: canal + busca + favoritos */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center',
        marginBottom: '16px',
      }}>
        <div style={{ maxWidth: '240px', minWidth: '180px' }}>
          <CustomDropdown
            options={canalFilterOptions}
            value={selectedCanal}
            onChange={setSelectedCanal}
            placeholder={t('prompts.filter_by_channel')}
          />
        </div>
        <div style={{ position: 'relative', flex: 1, minWidth: '180px', maxWidth: '340px' }}>
          <Search size={14} style={{
            position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
            color: 'var(--dp-text-tertiary)',
          }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('prompts.search_prompts')}
            style={{
              width: '100%', padding: '9px 12px 9px 34px',
              background: 'var(--dp-bg-input)', border: '1px solid var(--dp-border-subtle)',
              borderRadius: '9px', color: 'var(--dp-text-primary)',
              fontFamily: 'var(--dp-font)', fontSize: '13px', outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <button
          type="button"
          onClick={() => setOnlyFavorites((v) => !v)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '7px 14px', borderRadius: '9999px',
            fontSize: '12px', fontWeight: onlyFavorites ? 600 : 400,
            fontFamily: 'var(--dp-font)', cursor: 'pointer',
            transition: 'all 0.2s ease', lineHeight: 1.4,
            border: onlyFavorites ? '1px solid rgba(251, 191, 36, 0.4)' : '1px solid var(--dp-border-subtle)',
            background: onlyFavorites ? 'rgba(251, 191, 36, 0.1)' : 'transparent',
            color: onlyFavorites ? '#FBBF24' : 'var(--dp-text-tertiary)',
          }}
        >
          <Star size={12} fill={onlyFavorites ? 'currentColor' : 'none'} />
          {t('prompts.only_favorites')}
        </button>
      </div>

      {/* Grid */}
      {loading ? (
        <div style={gridStyle}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="dp-skeleton" style={{ height: '150px', borderRadius: '12px' }} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          padding: '24px 20px', textAlign: 'center', borderRadius: '12px',
          border: '1px dashed var(--dp-border-medium)',
          color: 'var(--dp-text-tertiary)', fontSize: '12.5px',
          fontFamily: 'var(--dp-font)', lineHeight: 1.5,
        }}>
          {prompts.length === 0 ? t('skills.my.empty') : t('skills.my.empty_filtered')}
        </div>
      ) : (
        <div style={gridStyle}>
          {filtered.map((skill) => (
            <SkillCard
              key={skill.id}
              skill={skill}
              variant="user"
              onOpen={setViewingSkill}
              onCopy={handleCopy}
              onEdit={openEdit}
              onDelete={handleDelete}
              onToggleFavorite={handleToggleFavorite}
              canalLabel={canalLabelOf(skill)}
            />
          ))}
        </div>
      )}

      {/* Modais */}
      <SkillDetailModal
        skill={viewingSkill}
        variant="user"
        onClose={() => setViewingSkill(null)}
        getDetail={getDetail}
        onEdit={openEdit}
        canalLabel={viewingSkill ? canalLabelOf(viewingSkill) : ''}
        isMobile={isMobile}
        isPrivate={isPrivate}
      />
      {userModal.open && (
        <UserSkillModal
          editing={userModal.editing}
          canais={canais}
          onClose={() => setUserModal({ open: false, editing: null })}
          onSaved={onChanged}
        />
      )}
    </SectionPanel>
  );
};

export default MySkillsSection;
